import { forwardRef } from 'react';
import { useTranslation } from 'next-i18next';
import { Button, Card } from '@components/common';
import { BellIcon, EnvelopeOpenIcon, CheckIcon } from '@heroicons/react/24/outline';

// Interface
// ******************************************
interface JobAlertCardProps {
  createFn: () => void,
  isSaved: boolean,
  isSaving: boolean,
}

/**
 * @description JobAlertCard component. To be used in job cards list.
 * Displays a card with information based on whether the job alert is saved or not, and provides
 * a button to create the job alert.
 *
 * - createFn: *function* - The function to call to create a job alert.
 * - isSaved: *boolean* - A flag indicating if the job alert is already saved.
 * - isSaving: *boolean* - A flag indicating if the job alert is in the process of being saved.
 */
export const JobAlertCard = forwardRef<HTMLDivElement, JobAlertCardProps>((props: JobAlertCardProps, ref) => {
  // Destructure props
  const {
    createFn,
    isSaved,
    isSaving,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Render component
  // *****************************************************
  return (
    <Card ref={ref} className="col-span-full rounded-md bg-surface" animation="riseUp">
      <Card.Body className="md:flex md:items-center md:space-x-4 md:p-6 lg:p-8 ">
        <div className="md:flex-1">
          <div className="flex space-x-3 md:items-center">
            <div className="size-12 shrink-0">
              {isSaved && <CheckIcon className="w-full text-secondary" />}
              {!isSaved && <EnvelopeOpenIcon className="w-full text-notificationInfo" />}
            </div>
            <div>
              <span className="block text-base font-bold">
                { isSaved ? t('saved.job_alert.banner.title') : t('save.job_alert.banner.title') }
              </span>
              <span className="text-sm">
                { isSaved ? t('saved.job_alert.banner.text') : t('save.job_alert.banner.text') }
              </span>
            </div>
          </div>
        </div>
        <Button
          type="button"
          className="mt-3 w-full md:mt-0 md:w-auto md:shrink-0"
          styling="outline"
          size="sm"
          color="primary-light"
          isLoading={isSaving}
          disabled={isSaving || isSaved}
          onClick={createFn}
        >
          {!isSaving && <BellIcon className="-ml-1 mr-3 size-5" />}
          { t(isSaved ? 'saved.job.alert' : 'job.list.create.alert') }
        </Button>
      </Card.Body>
    </Card>
  );
});
